import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const PartDetailPanel = ({ part, onClose, onLogUsage, onRequestProcurement }) => {
  const [usageHistory, setUsageHistory] = useState([]);
  const [procurementRequests, setProcurementRequests] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  useEffect(() => {
    fetchUsageHistory();
    fetchProcurementRequests();
  }, [part?.id]);

  const fetchUsageHistory = async () => {
    try {
      const { data, error } = await supabase
        ?.from('parts_usage_log')
        ?.select('*')
        ?.eq('part_id', part?.id)
        ?.order('used_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setUsageHistory(data || []);
    } catch (err) {
      console.error('Error fetching usage history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const fetchProcurementRequests = async () => {
    try {
      const { data, error } = await supabase
        ?.from('procurement_requests')
        ?.select('*')
        ?.eq('part_id', part?.id)
        ?.order('requested_at', { ascending: false })
        ?.limit(5);

      if (error) throw error;
      setProcurementRequests(data || []);
    } catch (err) {
      console.error('Error fetching procurement requests:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_stock':
        return 'bg-success/10 text-success border-success';
      case 'low_stock':
        return 'bg-warning/10 text-warning border-warning';
      case 'out_of_stock':
        return 'bg-error/10 text-error border-error';
      case 'on_order':
        return 'bg-primary/10 text-primary border-primary';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted';
    }
  };

  const getProcurementStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'approved':
        return 'bg-primary/10 text-primary';
      case 'ordered':
        return 'bg-primary/10 text-primary';
      case 'received':
        return 'bg-success/10 text-success';
      case 'cancelled':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted/10 text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <div className="bg-card border border-border rounded-t-lg sm:rounded-lg shadow-lg w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-foreground">{part?.part_name}</h2>
              <p className="text-sm text-muted-foreground mt-1">{part?.part_number}</p>
              <div className="mt-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
                    part?.status
                  )}`}
                >
                  {part?.status?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Part Details */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">Part Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Category</p>
                <p className="text-sm font-medium text-foreground">{part?.category}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Quantity</p>
                <p className="text-sm font-medium text-foreground">
                  {part?.quantity} / {part?.min_quantity} min
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unit Cost</p>
                <p className="text-sm font-medium text-foreground">
                  ${parseFloat(part?.unit_cost || 0)?.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-sm font-medium text-foreground">
                  ${(parseFloat(part?.unit_cost || 0) * parseInt(part?.quantity || 0))?.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="text-sm font-medium text-foreground">{part?.location || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Supplier</p>
                <p className="text-sm font-medium text-foreground">{part?.supplier || 'N/A'}</p>
              </div>
              {part?.barcode && (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Barcode</p>
                  <p className="text-sm font-medium text-foreground font-mono">{part?.barcode}</p>
                </div>
              )}
              {part?.description && (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="text-sm text-foreground">{part?.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="default"
                onClick={onLogUsage}
                disabled={part?.quantity === 0}
              >
                <Icon name="Minus" size={18} className="mr-2" />
                Log Usage
              </Button>
              <Button variant="outline" onClick={onRequestProcurement}>
                <Icon name="ShoppingCart" size={18} className="mr-2" />
                Request Procurement
              </Button>
            </div>
          </div>

          {/* Usage History */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">Recent Usage History</h3>
            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              </div>
            ) : usageHistory?.length > 0 ? (
              <div className="space-y-3">
                {usageHistory?.map((usage) => (
                  <div key={usage?.id} className="bg-muted/30 border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">
                          Used {usage?.quantity_used} units
                        </p>
                        {usage?.unit_number && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Unit: {usage?.unit_number}
                          </p>
                        )}
                        {usage?.technician_name && (
                          <p className="text-xs text-muted-foreground">
                            Technician: {usage?.technician_name}
                          </p>
                        )}
                        {usage?.usage_notes && (
                          <p className="text-xs text-muted-foreground mt-1">{usage?.usage_notes}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-foreground">
                          ${parseFloat(usage?.cost_total || 0)?.toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(usage?.used_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No usage history</p>
            )}
          </div>

          {/* Procurement Requests */}
          {procurementRequests?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Procurement Requests</h3>
              <div className="space-y-3">
                {procurementRequests?.map((request) => (
                  <div key={request?.id} className="bg-muted/30 border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-foreground">
                            {request?.requested_quantity} units
                          </p>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getProcurementStatusColor(
                              request?.status
                            )}`}
                          >
                            {request?.status}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Urgency: {request?.urgency}
                        </p>
                        {request?.requester_name && (
                          <p className="text-xs text-muted-foreground">
                            Requested by: {request?.requester_name}
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(request?.requested_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartDetailPanel;
